import { zxcvbn, zxcvbnOptions } from '@zxcvbn-ts/core';
import * as zxcvbnCommonPackage from '@zxcvbn-ts/language-common'
import * as zxcvbnEnPackage from '@zxcvbn-ts/language-en'
import * as zxcvbnFrPackage from '@zxcvbn-ts/language-fr'
import I18n from './i18n';
import theme from '../theme-config';
import WizzeeLog from './wizzeeLog';

// No translations set → feedback keys are plain short strings (e.g. "topTen", "useWords")
// defined in @zxcvbn-ts/core/dist/data/translationKeys.js.
// We translate them ourselves via I18n below.
zxcvbnOptions.setOptions({
  dictionary: {
    ...zxcvbnCommonPackage.dictionary,
    ...zxcvbnEnPackage.dictionary,
    // recommended the language of the country that the user will be in
    ...zxcvbnFrPackage.dictionary,
  },
  graphs: zxcvbnCommonPackage.adjacencyGraphs,
  useLevenshteinDistance: true,
});

class PasswordService {
  static MIN_PROGRESS_TO_SUBMIT = 80;

  static _color(score) {
    switch (score) {
      case 0:
      default:
        return theme.passwordStrength.error;
      case 1:
        return theme.passwordStrength.error;
      case 2:
        return theme.passwordStrength.warning;
      case 3:
        return theme.passwordStrength.notice;
      case 4:
        return theme.passwordStrength.ok;
    }
  }

  static matchAndTranslateWarning(warning) {
    switch (warning) {
      // dictionary
      case 'topTen':
        return I18n.t('zxcvbn.warnings.top-10');
      case 'topHundred':
        return I18n.t('zxcvbn.warnings.top-100');
      case 'common':
        return I18n.t('zxcvbn.warnings.common');
      case 'similarToCommon':
        return I18n.t('zxcvbn.warnings.similar');
      case 'wordByItself':
        return I18n.t('zxcvbn.warnings.word');
      case 'namesByThemselves':
        return I18n.t('zxcvbn.warnings.sole-name');
      case 'commonNames':
        return I18n.t('zxcvbn.warnings.name');
      // spatial
      case 'straightRow':
        return I18n.t('zxcvbn.warnings.straight-row');
      case 'keyPattern':
        return I18n.t('zxcvbn.warnings.short-pattern');
      // repeat
      case 'simpleRepeat':
        return I18n.t('zxcvbn.warnings.repeat-single');
      case 'extendedRepeat':
        return I18n.t('zxcvbn.warnings.repeat-multiple');
      // sequence
      case 'sequences':
        return I18n.t('zxcvbn.warnings.sequence');
      // regex
      case 'recentYears':
        return I18n.t('zxcvbn.warnings.recent-year');
      // date
      case 'dates':
        return I18n.t('zxcvbn.warnings.date');
      case 'pwned':
        return I18n.t('zxcvbn.warnings.pwned');
      case 'userInputs':
        return I18n.t('zxcvbn.warnings.userInputs');
      case null:
      case '':
        return I18n.t('zxcvbn.warnings.strength');
      default:
        WizzeeLog.warn('warning was not matched', warning);
        return warning;
    }
  }

  static matchAndTranslateSuggestion(suggestion) {
    switch (suggestion) {
      case 'useWords':
        return I18n.t('zxcvbn.suggestion.few-words');
      case 'noNeed':
        return I18n.t('zxcvbn.suggestion.no-need-digits');
      case 'anotherWord':
        return I18n.t('zxcvbn.suggestion.add-word');
      case 'longerKeyboardPattern':
        return I18n.t('zxcvbn.suggestion.longer-pattern');
      case 'repeated':
        return I18n.t('zxcvbn.suggestion.avoid-repeated');
      case 'sequences':
        return I18n.t('zxcvbn.suggestion.avoid-sequence');
      case 'recentYears':
        return I18n.t('zxcvbn.suggestion.avoid-recent-year');
      case 'associatedYears':
        return I18n.t('zxcvbn.suggestion.avoid-year-associated');
      case 'dates':
        return I18n.t('zxcvbn.suggestion.avoid-date-year-associated');
      case 'capitalization':
        return I18n.t('zxcvbn.suggestion.capitalization');
      case 'allUppercase':
        return I18n.t('zxcvbn.suggestion.all-uper');
      case 'reverseWords':
        return I18n.t('zxcvbn.suggestion.reversed');
      case 'l33t':
        return I18n.t('zxcvbn.suggestion.l33t');
      case 'pwned':
        return I18n.t('zxcvbn.suggestion.pwned');
      default:
        WizzeeLog.warn('suggestion was not matched', suggestion);
        return suggestion;
    }
  }

  static isExcludedSuggestion(suggestion) {
    const excludedSuggestions = ['noNeed'];
    return !excludedSuggestions.includes(suggestion);
  }

  static strengthIndicators(password) {
    password = password || '';
    const {
      score,
      feedback: { warning, suggestions },
    } = zxcvbn(password);

    return {
      color: this._color(score),
      progress: password === '' ? 0 : ((score + 1) / 5) * 100, // between 20 and 100
      warning: this.matchAndTranslateWarning(warning),
      suggestions: suggestions
      .filter(suggestion => this.isExcludedSuggestion(suggestion))
      .map(this.matchAndTranslateSuggestion),
    };
  }
}

export default PasswordService;
